import { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { loadRemote } from '@module-federation/runtime';
import { keyBy } from 'es-toolkit';
import { initMF } from '@newo/toolkit/module-federation/federation';
import { TAccount } from '@newo/api/gateway';
import { useAccountQuery } from '@/services/customer/account/account.queries.ts';
import { useWidgetsQuery } from '@/services/bff/bff.queries.ts';
import NoRecords from '@/components/NoRecords';

type TWidgetConfig = {
    remoteSourceUrl?: string;
    clientSecret?: string;
};
export const useWidgetsContent = () => {
    const {
        data: widgets,
        isLoading: isWidgetsLoading,
        isSuccess
    } = useWidgetsQuery({
        widget_type: 'internal'
    });

    const { data: account, isLoading: isAccountLoading } = useAccountQuery();
    const [isComponentLoading, setIsComponentLoading] = useState(false);
    const widgetConfig = useMemo(() => {
        const config: TWidgetConfig = {};

        if (!widgets?.length) {
            return config;
        }

        const workflowWidget = widgets.find((widget) => {
            const settings = keyBy(widget.settings, (w) => w.idn);
            return settings.widget_idn.value === 'workflow_builder';
        });

        if (workflowWidget) {
            const settings = keyBy(workflowWidget.settings, (w) => w.idn);
            config.remoteSourceUrl = settings.widget_source_url.value as string;
            config.clientSecret = settings.client_secret.value as string;
        }

        return config;
    }, [widgets]);

    const [Component, setComponent] = useState<FC<{
        clientSecret: string;
        account: TAccount;
    }> | null>();

    const isContentLoading =
        isWidgetsLoading || isComponentLoading || isAccountLoading;
    useEffect(() => {
        if (!isSuccess) {
            return;
        }

        if (!widgetConfig.clientSecret) {
            return;
        }

        setIsComponentLoading(true);
        // eslint-disable-next-line promise/catch-or-return
        initMF({
            remoteSourceUrl: import.meta.env.VITE_FLOW_BUILDER_REMOTE_URL,
            fallbackUrl: import.meta.env.VITE_FLOW_BUILDER_REMOTE_URL,
            remoteSourceName: 'flow-builder-remote'
        })
            .then(async () => {
                return loadRemote<{
                    default: FC<{
                        clientSecret: string;
                        account: TAccount;
                    }>;
                }>('flow-builder-remote/WorkflowBuilderRemote');
            })
            .then((module) => {
                if (module) {
                    setComponent(() => module.default);
                }
            })
            .finally(() => {
                setIsComponentLoading(false);
            });
    }, [isSuccess, widgetConfig.clientSecret, widgetConfig.remoteSourceUrl]);

    const content = useMemo(() => {
        if (isContentLoading) {
            return;
        }

        if (!widgetConfig.clientSecret || !account) {
            return <NoRecords />;
        }

        if (!Component) {
            return (
                <div className="flex items-center justify-center py-16">
                    The static file for widget is not found
                </div>
            );
        }

        return (
            <Suspense fallback={<div>Loading Widgets...</div>}>
                <Component
                    account={account}
                    clientSecret={widgetConfig.clientSecret}
                />
            </Suspense>
        );
    }, [isContentLoading, widgetConfig.clientSecret, Component, account]);

    return { content, isLoading: isContentLoading };
};
